import React, { useMemo } from 'react';
import { Card, Row } from 'react-bootstrap';
import ServerError from '../../../../components/ServerError';
import { useManagerEmployeeReviewsQuery } from '../../__generated__/graphql';
import { EngagementContainer } from '../EngagementContainer';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { useFlairBreadcrumbHook } from '../../../../hooks/useFlairBreadcrumbHook';
import { LoadingEmployeeFeedbacks } from '../../pages/PerformanceReview/EmployeePerformanceReview/EmployeeFeedbacks/LoadingEmployeeFeedbacks';
import { EmptyEmployeeFeedbacks } from '../../pages/PerformanceReview/EmployeePerformanceReview/EmployeeFeedbacks/EmptyEmployeeFeedbacks';
import {
  FeedbackFilter,
  collectCycleNameFilterOptions,
  collectRevieweeFilterOptions,
  collectStatusFilterOptions,
  collectYearFilterOptions,
} from '../../pages/PerformanceReview/Reviews/Filter/FeedbackFilter';
import { useFlairPersistentFilter } from '../../../../hooks/useFlairPersistentFilter';
import { applyFeedbackFilter } from '../../pages/PerformanceReview/Reviews/filters';
import { emptyFeedbackFilter } from '../../pages/PerformanceReview/Reviews/types';
import { managerRoutes } from '../routes';
import { mapManagerFeedback, sortManagerFeedbacks } from './mappings';
import { ManagerReviewsTable } from './ManagerReviewsTable';

const ReviewsList: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { data, loading, error } = useManagerEmployeeReviewsQuery();
  const t = useNamespacedTranslation('navigation.menuItems.teamDevelopment');
  const reviewsSubTitle = t('subMenuItems.reviews.title');

  useFlairBreadcrumbHook([{ label: t('title') }, { label: reviewsSubTitle }]);

  const allFeedbacks = useMemo(
    () => (data?.manager.performanceReviews ?? []).map(mapManagerFeedback),
    [data],
  );

  const [filter, setFilter] = useFlairPersistentFilter({
    defaultFilter: emptyFeedbackFilter,
    storageKey: 'manager_feedback_filter',
    createRouteUrl: (queryParams) =>
      managerRoutes.reviews.route.withQueryParams(queryParams).create({}),
  });

  const filteredFeedbacks = useMemo(
    () => sortManagerFeedbacks(applyFeedbackFilter(filter, allFeedbacks)),
    [filter, allFeedbacks],
  );

  const cycleNameOptions = useMemo(
    () => collectCycleNameFilterOptions(allFeedbacks),
    [allFeedbacks],
  );
  const yearOptions = useMemo(
    () => collectYearFilterOptions(allFeedbacks),
    [allFeedbacks],
  );
  const statusOptions = useMemo(
    () => collectStatusFilterOptions(allFeedbacks),
    [allFeedbacks],
  );
  const revieweeOptions = useMemo(
    () => collectRevieweeFilterOptions(allFeedbacks),
    [allFeedbacks],
  );

  if (error) {
    return <ServerError />;
  }

  if (loading || data === undefined) {
    return (
      <EngagementContainer title={reviewsSubTitle}>
        <LoadingEmployeeFeedbacks />
      </EngagementContainer>
    );
  }

  if (!data.manager.performanceReviews.length) {
    return (
      <EngagementContainer title={reviewsSubTitle}>
        <EmptyEmployeeFeedbacks />
      </EngagementContainer>
    );
  }

  return (
    <EngagementContainer title={reviewsSubTitle}>
      <Row>
        <Card>
          <Card.Header className="h-auto">
            <FeedbackFilter
              onChange={setFilter}
              filter={filter}
              cycleNameOptions={cycleNameOptions}
              reviewerOptions={null}
              yearOptions={yearOptions}
              statusOptions={statusOptions}
              revieweeOptions={revieweeOptions}
            />
          </Card.Header>
          <ManagerReviewsTable feedbacks={filteredFeedbacks} />
        </Card>
      </Row>
    </EngagementContainer>
  );
};

export default ReviewsList;
